import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { FORM_TOKENS } from "./FormDesignTokens";

export interface FormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: "transaction" | "standard" | "compact";
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  badge?: React.ReactNode;
  headerActions?: React.ReactNode;
  footerLeft?: React.ReactNode;
  footerRight?: React.ReactNode;
  children: React.ReactNode;
  trigger?: React.ReactNode;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  className?: string;
}

export function FormModal({
  open,
  onOpenChange,
  size = "transaction",
  icon,
  title,
  description,
  badge,
  headerActions,
  footerLeft,
  footerRight,
  children,
  trigger,
  onKeyDown,
  className = "",
}: FormModalProps) {
  const sizeClass = {
    transaction: "max-w-[1050px] w-[96vw] lg:w-full h-[95vh] sm:h-[90vh] max-h-[90vh] rounded-xl sm:rounded-2xl",
    standard: "max-w-2xl w-[94vw] sm:w-full max-h-[88vh] rounded-xl sm:rounded-2xl",
    compact: "max-w-md w-[92vw] sm:w-full max-h-[85vh] rounded-xl",
  }[size];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className={`flex flex-col p-0 overflow-hidden border border-slate-200 shadow-xl bg-white rounded-xl sm:rounded-2xl [&>button.absolute]:hidden ${sizeClass} ${className}`}
        onKeyDown={onKeyDown}
      >
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-5 py-3.5 sm:px-7 sm:py-4 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {icon && (
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100 shadow-2xs">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className={FORM_TOKENS.typography.modalTitle}>
                  {title}
                </DialogTitle>
                {badge && <div className="shrink-0">{badge}</div>}
              </div>
              {description && (
                <p className={`${FORM_TOKENS.typography.modalSubtitle} truncate max-w-xl`}>
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-3">
            {headerActions}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100"
              title="Close (Esc)"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Independently Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6 space-y-5 sidebar-scrollbar bg-slate-50/40">
          {children}
        </div>

        {/* Fixed / Stable Footer Actions */}
        {(footerLeft || footerRight) && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-5 py-3.5 sm:px-7 sm:py-4 border-t border-slate-200 bg-slate-50/80 shrink-0">
            <div className="flex items-center gap-2 justify-start order-2 sm:order-1">
              {footerLeft}
            </div>
            <div className="flex items-center gap-2 justify-end order-1 sm:order-2">
              {footerRight}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
